// src/lib/firebase.ts
// Firebase initialization — single source of truth.
// Never call initializeApp anywhere else.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  getFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  uploadBytes,
  type FirebaseStorage,
  type StorageReference,
} from 'firebase/storage';

const firebaseConfig = {
  apiKey:            'AIzaSyBRNMXu8w6Y_w1XPH4XNUvVkQ4gxjXhrq4',
  authDomain:        'espforsports.com',
  projectId:         'start-f3a45',
  storageBucket:     'start-f3a45.firebasestorage.app',
  messagingSenderId: '315379404054',
  appId:             '1:315379404054:web:cbcb3c847becfb086b5437',
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);

// Firestore with persistent multi-tab cache (3-level fallback).
let _db: Firestore;
try {
  _db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
  console.log('✅ Firestore persistence enabled (multi-tab)');
} catch (err) {
  console.warn('⚠️ Multi-tab persistence unavailable, falling back:', err);
  try {
    _db = initializeFirestore(app, { localCache: persistentLocalCache() });
    console.log('✅ Firestore persistence enabled (single-tab)');
  } catch (fallbackErr) {
    console.warn('⚠️ Firestore persistence unavailable, using default:', fallbackErr);
    _db = getFirestore(app);
  }
}
export const db: Firestore = _db;

export const storage: FirebaseStorage = getStorage(app);

/**
 * Authenticated upload helper. Uses Firebase SDK first, falls back to direct
 * REST upload with Bearer token if SDK upload throws.
 */
export async function uploadFileWithToken(
  storageRef: StorageReference,
  file: File
): Promise<unknown> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken();
  const metadata = {
    contentType: file.type,
    customMetadata: {
      uploadedBy: user.uid,
      uploadedAt: Date.now().toString(),
      fileName: file.name,
    },
  };
  try {
    return await uploadBytes(storageRef, file, metadata);
  } catch (sdkErr) {
    console.warn('[uploadFileWithToken] SDK upload failed, falling back to REST:', sdkErr);
    const uploadUrl =
      `https://firebasestorage.googleapis.com/v0/b/${storageRef.bucket}/o` +
      `?name=${encodeURIComponent(storageRef.fullPath)}`;
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }
}